import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Target,
  ChevronDown,
  Info,
  AlertCircle,
  ExternalLink,
  Play,
  ShieldCheck,
  Table2,
  CheckCircle2,
  RefreshCw,
  MapPin,
  ClipboardList,
} from "lucide-react";
import { ApiService } from "../../services/api";
import type {
  YksBeyanDurumu,
  YksDeneme,
  YksDers,
  YksEtiket,
  YksGerceklikDurum,
  YksKaynak,
  YksKonu,
  YksMevcutKaynakKanitli,
  YksPuanTuru,
  YksRoadmapResponse,
  YksScenarioResponse,
  YksSenaryoBant,
} from "../../types/navigator";

// "Yol Haritam" — hedefe göre konu önceliği. Koç burada çözdürmez; hangi konuya önce
// çalışılacağını söyler, "Çalış" mevcut E-Eğitim oynatıcısına gider.
// İKİ SINAV: YKS (hedef = sıralama, oturumlar TYT/AYT, puan türü seçilir) ve
// LGS (hedef = yüzdelik dilim, oturumlar Sözel/Sayısal, tek puan türü, konu atlama kapalı).
// Sınava özgü her şey sunucudan gelir (sinav, hedefBirimi, oturumlar, oturumTestleri).
// Veri: ÖSYM / MEB sayısal bilgileri + yıllık konu dağılımı; yaklaşık değerlerdir.

const PUAN_TURLERI: YksPuanTuru[] = ["TYT", "SAY", "EA", "SÖZ"];

const nfInt = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0 });
const nf1 = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 1 });

const pct = (x: number) => Math.round((x || 0) * 100);

const ETIKET: Record<YksEtiket, { label: string; chip: string; bar: string }> = {
  ONCELIKLI: { label: "Öncelikli", chip: "border-emerald-200 bg-emerald-50 text-emerald-700", bar: "bg-emerald-500" },
  SONRA: { label: "Sonra", chip: "border-amber-200 bg-amber-50 text-amber-700", bar: "bg-amber-500" },
  ATLANABILIR: { label: "Atlanabilir", chip: "border-gray-200 bg-gray-50 text-gray-500", bar: "bg-gray-400" },
};

// Tailwind sınıfları tam yazılmalı (JIT); bant → arka plan eşlemesi.
const BANT: Record<YksSenaryoBant, string> = {
  "koyu-yesil": "bg-emerald-200 text-emerald-900",
  yesil: "bg-emerald-100 text-emerald-800",
  sari: "bg-amber-100 text-amber-800",
  turuncu: "bg-orange-100 text-orange-800",
  gri: "bg-gray-100 text-gray-500",
};

const INPUT_CLS =
  "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none transition-colors focus:border-blue-400 disabled:bg-gray-50";

// "300000" / "300.000" → 300000
const parseTamsayi = (t: string): number | null => {
  const d = String(t || "").replace(/\D/g, "");
  return d ? parseInt(d, 10) : null;
};

// "12,5" / "12.5" → 12.5
const parseNet = (t: string): number | null => {
  const s = String(t || "").trim().replace(",", ".");
  if (!s) return null;
  const v = parseFloat(s);
  return Number.isFinite(v) ? v : null;
};

// mevcutNetler ya oturum toplamı ({TYT: 60, AYT: 20} / {Sözel: 30, Sayısal: 25}) ya da
// ders bazlı ({"TYT Türkçe": 25} / {"Matematik": 14}) gelebilir; formda oturum toplamı gösteriyoruz.
// Oturumun hangi dersleri kapsadığını sunucu söyler (oturumTestleri) — ad öneki varsayılmaz.
const netToplam = (
  m: Record<string, number> | null | undefined,
  oturum: string,
  testler: string[] | undefined
): number | null => {
  if (!m) return null;
  if (typeof m[oturum] === "number") return m[oturum];
  let sum = 0;
  let hit = false;
  for (const t of testler || []) {
    if (typeof m[t] === "number") {
      sum += m[t];
      hit = true;
    }
  }
  return hit ? Math.round(sum * 100) / 100 : null;
};

// "%5" / "5,5" → 5.5 (LGS hedefi yüzdelik dilim)
const parseYuzde = (t: string): number | null => {
  const s = String(t || "").replace("%", "").trim().replace(",", ".");
  if (!s) return null;
  const v = parseFloat(s);
  return Number.isFinite(v) && v > 0 && v <= 100 ? v : null;
};

const yuzdeText = (n: number | null | undefined) => (n == null ? "" : `%${String(n).replace(".", ",")}`);

const nf2 = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 });
const netText = (n: number | null) => (n === null ? "" : nf2.format(n));

// Mevcut durum kaynağı — satır başlığı + cümle içi adı
const KAYNAK: Record<YksKaynak, { label: string; cumle: string }> = {
  deneme: { label: "Son deneme", cumle: "son denemen" },
  beyan: { label: "Beyanın", cumle: "beyanın" },
  kanitli: { label: "Testlerinden kanıtlı", cumle: "testlerinden kanıtlı net" },
};

const BEYAN_LABEL: Record<YksBeyanDurumu, string> = {
  bilmiyorum: "bilmiyorum",
  duydum: "duydum",
  cozerim: "çözerim",
  iyiyim: "iyiyim",
};

// Gerçekçilik uyarısı — durum → renk (Tailwind sınıfları tam yazılır)
const GERCEKLIK: Record<YksGerceklikDurum, string> = {
  ustunde: "border-emerald-200 bg-emerald-50 text-emerald-800",
  yakin: "border-blue-200 bg-blue-50 text-blue-800",
  orta: "border-amber-200 bg-amber-50 text-amber-800",
  uzak: "border-rose-200 bg-rose-50 text-rose-800",
  bilinmiyor: "border-gray-200 bg-gray-50 text-gray-600",
};

// Veri paketindeki kaynak anahtarları → ekranda görünen ad.
const KAYNAK_ADI: Record<string, string> = {
  osym_sayisal_bilgiler_2026: "ÖSYM Sayısal Bilgiler",
  katsayilar: "Katsayı kaynağı",
  meb_2026_rapor: "MEB Merkezî Sınav Raporu",
  katsayilar_ve_dilim: "Katsayı ve dilim kaynağı",
};

const dateFmt = new Intl.DateTimeFormat("tr-TR", { day: "numeric", month: "short", year: "numeric" });
const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : dateFmt.format(d);
};

// Sayıdan sonra iyelik eki, okunuşa göre: 18'i, 40'ı, 20'si, 100'ü, 1000'i
const IYELIK_BIRLER: Record<string, string> = { "1": "'i", "2": "'si", "3": "'ü", "4": "'ü", "5": "'i", "6": "'sı", "7": "'si", "8": "'i", "9": "'u" };
const IYELIK_ONLAR: Record<string, string> = { "1": "'u", "2": "'si", "3": "'u", "4": "'ı", "5": "'si", "6": "'ı", "7": "'i", "8": "'i", "9": "'ı" };
const iyelik = (n: number): string => {
  const s = String(Math.abs(Math.trunc(n)));
  const sifir = (/0*$/.exec(s) || [""])[0].length;
  if (sifir === s.length) return "'ı"; // sıfır
  if (sifir === 0) return IYELIK_BIRLER[s[s.length - 1]] || "'i";
  if (sifir === 1) return IYELIK_ONLAR[s[s.length - 2]] || "'i";
  if (sifir === 2) return "'ü"; // yüz
  if (sifir <= 5) return "'i"; // bin
  return "'u"; // milyon
};

// ── Konu satırı ──
function KonuRow({
  konu,
  expanded,
  onToggle,
  resolveKonu,
  onStudy,
  onBeyan,
  beyanBusy,
}: {
  konu: YksKonu;
  expanded: boolean;
  onToggle: () => void;
  resolveKonu: (id: string) => string;
  onStudy: (lessonId: string) => void;
  onBeyan: (konuId: string, durum: YksBeyanDurumu | null) => void;
  beyanBusy: boolean;
}) {
  const et = ETIKET[konu.etiket] || ETIKET.SONRA;
  const kaynak = konu.hakimiyetKaynagi || null;
  const beyanAktif = konu.beyanDurumu === "iyiyim";
  const testVerisi = kaynak === "test";
  const barVar = konu.hakimiyet > 0 || !!kaynak;
  const barYazi =
    kaynak === "test"
      ? `testlerinden %${pct(konu.hakimiyet)}`
      : kaynak === "beyan"
      ? `beyanın: ${(konu.beyanDurumu && BEYAN_LABEL[konu.beyanDurumu]) || konu.beyanDurumu || "—"}`
      : `%${pct(konu.hakimiyet)} hakimiyet`;

  return (
    <div className="border-t border-gray-100 first:border-t-0">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full flex-col gap-1 px-3 py-2 text-left transition-colors hover:bg-gray-50"
      >
        <div className="flex w-full items-center gap-2">
          <span className="w-5 shrink-0 text-[11px] tabular-nums text-gray-400">{konu.sira}</span>
          <span className="min-w-0 flex-1 text-sm text-gray-800">{konu.konu}</span>
          <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium ${et.chip}`}>{et.label}</span>
          <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-gray-300 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </div>

        <div className="flex w-full flex-wrap items-center gap-x-2 gap-y-1 pl-7 text-[11px] text-gray-500">
          <span className="whitespace-nowrap">{nf1.format(konu.beklenenSoru)} soru/yıl</span>
          <span>·</span>
          <span className="whitespace-nowrap">%{pct(konu.cikmaSikligi)} çıkma</span>
          <span>·</span>
          <span className="whitespace-nowrap font-medium text-gray-700">+{nf1.format(konu.puanKatkisi)} puan</span>
          {konu.garanti && (
            <span className="inline-flex items-center gap-0.5 rounded-full border border-blue-200 bg-blue-50 px-1.5 py-px text-[10px] font-medium text-blue-700">
              <ShieldCheck className="h-3 w-3" /> garanti
            </span>
          )}
          {!konu.icerikVar && (
            <span className="rounded-full border border-dashed border-gray-300 bg-gray-50 px-1.5 py-px text-[10px] text-gray-500">
              içerik yok
            </span>
          )}
        </div>

      </button>

      {/* Hakimiyet + beyan — dış düğmenin dışında (iç içe düğme olmaz) */}
      <div className="flex items-center gap-2 px-3 pb-2 pl-10">
        {barVar ? (
          <>
            <div className="h-1 flex-1 overflow-hidden rounded-full bg-gray-100">
              <div className={`h-full rounded-full ${et.bar}`} style={{ width: `${Math.min(100, pct(konu.hakimiyet))}%` }} />
            </div>
            <span className="shrink-0 text-[10px] tabular-nums text-gray-400">{barYazi}</span>
          </>
        ) : (
          <span className="flex-1 text-[10px] text-gray-400">henüz ölçülmedi</span>
        )}
        <button
          type="button"
          onClick={() => onBeyan(konu.konuId, beyanAktif ? null : "iyiyim")}
          disabled={testVerisi || beyanBusy}
          title={testVerisi ? "Test verisi beyanı ezer" : beyanAktif ? "İşareti kaldır" : "Bu konuyu bildiğini işaretle"}
          className={`shrink-0 rounded-md border px-2 py-0.5 text-[10px] font-medium transition-colors disabled:opacity-50 ${
            beyanAktif ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-gray-200 bg-white text-gray-600 hover:border-blue-300"
          }`}
        >
          {beyanBusy ? "…" : beyanAktif ? "Biliyorum ✓" : "Biliyorum"}
        </button>
      </div>

      {expanded && (
        <div className="space-y-2 bg-gray-50/70 px-3 py-2.5 pl-10 text-[11px] text-gray-600">
          {konu.seri.length > 0 && (
            <p>
              <span className="text-gray-400">Yıllara göre soru: </span>
              {konu.seri.map((s, i) => (
                <span key={s.yil} className="whitespace-nowrap">
                  {i > 0 && " · "}
                  {s.yil}: {s.soru === null ? "–" : s.soru}
                </span>
              ))}
            </p>
          )}
          {konu.haricYillar.length > 0 && (
            <p className="text-gray-500">{konu.haricYillar.join(", ")} müfredat daraltması nedeniyle sayılmadı.</p>
          )}
          {konu.onKosullar.length > 0 && (
            <p>
              <span className="text-gray-400">Önce: </span>
              {konu.onKosullar.map(resolveKonu).join(", ")}
            </p>
          )}

          {konu.bolumler.length > 0 ? (
            <div className="space-y-1">
              {konu.bolumler.map((b) => (
                <div key={b.sectionId} className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs text-gray-800">{b.title}</p>
                    <p className="truncate text-[10px] text-gray-400">
                      {b.lessonTitle}
                      {b.minutes > 0 && ` · ${b.minutes} dk`}
                    </p>
                  </div>
                  <span className={`shrink-0 text-[10px] font-medium ${b.mastery === null ? "text-gray-400" : "text-gray-700"}`}>
                    {b.mastery === null ? "çözülmedi" : `%${Math.round(b.mastery)}`}
                  </span>
                  <button
                    type="button"
                    onClick={() => onStudy(b.lessonId)}
                    className="inline-flex shrink-0 items-center gap-1 rounded-md bg-blue-600 px-2 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-blue-700"
                  >
                    <Play className="h-3 w-3" /> Çalış
                  </button>
                </div>
              ))}
            </div>
          ) : (
            !konu.icerikVar && <p className="text-gray-400">Bu konunun içeriği henüz sistemde yok.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Ders kartı ──
function DersCard({
  ders,
  expanded,
  onToggle,
  expandedKonu,
  onToggleKonu,
  resolveKonu,
  onStudy,
  onBeyan,
  beyanBusy,
}: {
  ders: YksDers;
  expanded: boolean;
  onToggle: () => void;
  expandedKonu: Set<string>;
  onToggleKonu: (id: string) => void;
  resolveKonu: (id: string) => string;
  onStudy: (lessonId: string) => void;
  onBeyan: (konuId: string, durum: YksBeyanDurumu | null) => void;
  beyanBusy: string | null;
}) {
  const konular = useMemo(() => [...ders.konular].sort((a, b) => a.sira - b.sira), [ders.konular]);

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-3 py-3 text-left transition-colors hover:bg-gray-50 sm:px-4"
      >
        <ChevronDown className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
        <span className="min-w-0 flex-1 text-sm font-semibold text-gray-900">
          {ders.test} {ders.ders}
        </span>
        {ders.dersAtlanabilir && (
          <span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[10px] font-medium text-gray-500">
            ders atlanabilir
          </span>
        )}
        <span className="basis-full pl-7 text-[11px] text-gray-500 sm:basis-auto sm:pl-0">
          <b className="text-gray-700">{ders.oncelikli}</b>/{ders.toplamKonu} öncelikli · %{pct(ders.kapsama)} kapsama
        </span>
      </button>

      {expanded && (
        <div className="border-t border-gray-100">
          {konular.length === 0 ? (
            <p className="px-3 py-3 text-xs text-gray-400">Bu derste konu bulunamadı.</p>
          ) : (
            konular.map((k) => (
              <KonuRow
                key={k.konuId}
                konu={k}
                expanded={expandedKonu.has(k.konuId)}
                onToggle={() => onToggleKonu(k.konuId)}
                resolveKonu={resolveKonu}
                onStudy={onStudy}
                onBeyan={onBeyan}
                beyanBusy={beyanBusy === k.konuId}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Neredesin — üç kaynak: son deneme, beyan, testlerden kanıtlı ──
function KaynakSatiri({
  baslik,
  kullanilan,
  children,
}: {
  baslik: string;
  kullanilan: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="border-t border-gray-100 py-2 first:border-t-0 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <span className="text-xs font-medium text-gray-800">{baslik}</span>
        {kullanilan && (
          <span className="rounded-full border border-blue-200 bg-blue-50 px-1.5 py-px text-[10px] font-medium text-blue-700">kullanılan</span>
        )}
      </div>
      <div className="mt-0.5 text-[11px] text-gray-500">{children}</div>
    </div>
  );
}

function KanitliDetay({ k }: { k: YksMevcutKaynakKanitli }) {
  const tests = Object.entries(k.olcum || {});
  if (tests.length === 0) return null;
  return (
    <ul className="mt-1.5 space-y-0.5 rounded-lg bg-gray-50 px-2.5 py-2">
      {tests.map(([test, o]) => {
        const net = k.netler?.[test];
        return (
          <li key={test} className="text-[11px] text-gray-600">
            <span className="font-medium text-gray-700">{test}:</span> {o.toplamSoru} sorunun {o.olculenSoru}
            {iyelik(o.olculenSoru)} ölçüldü{typeof net === "number" && `, net ${nf1.format(net)}`}
          </li>
        );
      })}
    </ul>
  );
}

function NeredesinPanel({ data }: { data: YksRoadmapResponse }) {
  const [kanitliOpen, setKanitliOpen] = useState(false);
  const mevcut = data.mevcut || null;
  const k = data.mevcutKaynaklar || { deneme: null, beyan: null, kanitli: null };
  const denemeler = data.denemeler || [];
  const varMi = !!(mevcut || denemeler.length || k.deneme || k.beyan || k.kanitli);
  const kullanilan = mevcut?.kaynak || null;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-2 flex items-center gap-2">
        <MapPin className="h-4 w-4 shrink-0 text-blue-600" />
        <h2 className="text-sm font-semibold text-gray-900">Neredesin</h2>
      </div>

      {!varMi ? (
        <p className="text-xs text-gray-500">
          Nerede olduğunu henüz bilmiyorum: bir deneme çöz, netlerini yaz ya da planındaki testleri çöz.
        </p>
      ) : (
        <div>
          {k.deneme && (
            <KaynakSatiri baslik={KAYNAK.deneme.label} kullanilan={kullanilan === "deneme"}>
              <span>{k.deneme.examName}</span>
              {k.deneme.date && <span> · {fmtDate(k.deneme.date)}</span>}
              <span>
                {" "}
                · <b className="text-gray-700">{nf1.format(k.deneme.puan)}</b> puan · ≈ {k.deneme.yerGosterim || k.deneme.siralamaGosterim}
              </span>
              {!!k.deneme.ekOturumlar?.length && (
                <span className="block text-[11px] text-gray-400">
                  {k.deneme.ekOturumlar.map((e) => `${e.oturum} netleri önceki denemeden (${e.examName})`).join(" · ")}
                </span>
              )}
            </KaynakSatiri>
          )}
          {k.beyan && (
            <KaynakSatiri baslik={KAYNAK.beyan.label} kullanilan={kullanilan === "beyan"}>
              <span>
                <b className="text-gray-700">{nf1.format(k.beyan.puan)}</b> puan · ≈ {k.beyan.yerGosterim || k.beyan.siralamaGosterim}
              </span>
              {k.beyan.at && <span> · {fmtDate(k.beyan.at)}</span>}
            </KaynakSatiri>
          )}
          {k.kanitli && (
            <KaynakSatiri baslik={KAYNAK.kanitli.label} kullanilan={kullanilan === "kanitli"}>
              <span>
                <b className="text-gray-700">{nf1.format(k.kanitli.puan)}</b> puan · ≈ {k.kanitli.yerGosterim || k.kanitli.siralamaGosterim} · {k.kanitli.konuSayisi} konu ölçüldü
              </span>
              {Object.keys(k.kanitli.olcum || {}).length > 0 && (
                <button
                  type="button"
                  onClick={() => setKanitliOpen((v) => !v)}
                  className="ml-2 inline-flex items-center gap-0.5 font-medium text-blue-600 hover:text-blue-700"
                >
                  {kanitliOpen ? "gizle" : "detay"}
                  <ChevronDown className={`h-3 w-3 transition-transform ${kanitliOpen ? "rotate-180" : ""}`} />
                </button>
              )}
              {kanitliOpen && <KanitliDetay k={k.kanitli} />}
            </KaynakSatiri>
          )}

          {kullanilan && (
            <p className="mt-2 flex items-start gap-1.5 border-t border-gray-100 pt-2 text-[11px] text-gray-500">
              <Info className="mt-px h-3.5 w-3.5 shrink-0 text-gray-400" />
              Hedefle karşılaştırmada {KAYNAK[kullanilan]?.cumle || kullanilan} kullanılıyor.
            </p>
          )}
          {!data.hasHedef && data.sinav !== "LGS" && data.puanTuruTahmini && (
            <p className="mt-1 text-[11px] text-gray-500">Denemelerine göre puan türün {data.puanTuruTahmini} görünüyor.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Denemelerim — yeniden eskiye; 3'ten fazlaysa kapalı başlar ──
function DenemelerCard({
  denemeler,
  hasHedef,
  gerekenPuan,
  oturumlar,
}: {
  denemeler: YksDeneme[];
  hasHedef: boolean;
  gerekenPuan: number | null;
  oturumlar: string[];
}) {
  const [open, setOpen] = useState(denemeler.length <= 3);

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-gray-50 sm:px-4"
      >
        <ClipboardList className="h-4 w-4 shrink-0 text-blue-600" />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-gray-900">Denemelerim</span>
          <span className="block text-[11px] text-gray-400">{denemeler.length} deneme · yeniden eskiye</span>
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-gray-100">
          {denemeler.map((d) => {
            const fark = hasHedef && gerekenPuan !== null ? gerekenPuan - d.puan : null;
            const ustunde = fark !== null && fark <= 0;
            const chip =
              fark === null
                ? "border-gray-200 bg-gray-50 text-gray-600"
                : ustunde
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : "border-amber-200 bg-amber-50 text-amber-700";
            return (
              <div key={d.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 border-t border-gray-100 px-3 py-2 first:border-t-0 sm:px-4">
                <span className="shrink-0 text-[11px] tabular-nums text-gray-400">{fmtDate(d.date) || "—"}</span>
                <span className="min-w-0 flex-1 truncate text-xs text-gray-800">{d.examName}</span>
                <span
                  title={`${d.bant.gosterim} bandı`}
                  className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium tabular-nums ${chip}`}
                >
                  ≈ {d.yerGosterim || d.siralamaGosterim}
                </span>
                <span className="basis-full text-[11px] text-gray-500">
                  {oturumlar.map((o) => `${o} ${nf1.format(d.toplam?.[o] ?? 0)}`).join(" · ")} ·{" "}
                  <b className="text-gray-700">{nf1.format(d.puan)}</b> puan
                  {!!d.ekOturumlar?.length && <span className="text-gray-400"> · {d.ekOturumlar.map((e) => e.oturum).join(", ")} önceki denemeden</span>}
                  {fark !== null && (
                    <span className={ustunde ? " text-emerald-600" : " text-amber-600"}>
                      {" "}
                      · {ustunde ? "hedefin üstünde" : `hedefe ${nf1.format(fark)} puan`}
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Net senaryoları ──
// Satır = birinci oturumun toplam neti, sütun = ikincininki (YKS: TYT × AYT · LGS: Sayısal × Sözel).
// YKS-TYT puan türünde ikinci oturum yoktur → tek sütunlu liste.
function ScenarioBlock({
  data,
  currentSatir,
  currentSutun,
  yerBasligi,
}: {
  data: YksScenarioResponse;
  currentSatir: number | null;
  currentSutun: number | null;
  yerBasligi: string;
}) {
  const sutunNetler = data.sutunNetler || data.aytNetler || [];
  const tekSutun = !data.sutunOturum || sutunNetler.length <= 1;
  const satirAd = data.satirOturum || "TYT";

  // Öğrencinin son deneme netine en yakın hücreyi işaretle.
  const nearest = (values: number[], v: number | null) => {
    if (v === null || values.length === 0) return null;
    return values.reduce((best, cur) => (Math.abs(cur - v) < Math.abs(best - v) ? cur : best), values[0]);
  };
  const satirNet = (r: { net?: number; tytNet: number }) => r.net ?? r.tytNet;
  const hucreNet = (h: { net?: number; aytNet: number }) => h.net ?? h.aytNet;
  const hlSatir = nearest(data.satirlar.map(satirNet), currentSatir);
  const hlSutun = tekSutun ? sutunNetler[0] ?? null : nearest(sutunNetler, currentSutun);

  if (tekSutun) {
    return (
      <div className="overflow-x-auto">
        <div className="min-w-[16rem] text-[11px]">
          <div className="grid grid-cols-2 border-b border-gray-200 pb-1 font-semibold text-gray-500">
            <span className="px-2">{satirAd} net</span>
            <span className="px-2">{yerBasligi}</span>
          </div>
          {data.satirlar.map((r) => {
            const h = r.hucreler[0];
            const hl = hlSatir === satirNet(r);
            return (
              <div key={satirNet(r)} className="grid grid-cols-2 border-b border-gray-100 last:border-b-0">
                <span className={`px-2 py-1 tabular-nums ${hl ? "font-semibold text-blue-700" : "text-gray-700"}`}>{satirNet(r)}</span>
                <span
                  title={h ? `${nf1.format(h.puan)} puan` : undefined}
                  className={`px-2 py-1 tabular-nums ${h ? BANT[h.bant] || BANT.gri : ""} ${hl ? "ring-2 ring-inset ring-blue-500" : ""}`}
                >
                  {h ? h.yerGosterim || h.gosterim : "–"}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="border-separate border-spacing-0 text-[11px]">
        <thead>
          <tr>
            <th className="sticky left-0 z-10 whitespace-nowrap border-b border-r border-gray-200 bg-white px-2 py-1 text-left font-semibold text-gray-500">
              {satirAd} \ {data.sutunOturum}
            </th>
            {sutunNetler.map((n) => (
              <th
                key={n}
                className={`whitespace-nowrap border-b border-gray-200 bg-white px-2 py-1 text-center font-semibold tabular-nums ${
                  hlSutun === n ? "text-blue-700" : "text-gray-500"
                }`}
              >
                {n}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.satirlar.map((r) => (
            <tr key={satirNet(r)}>
              <th
                className={`sticky left-0 z-10 whitespace-nowrap border-b border-r border-gray-100 bg-white px-2 py-1 text-left font-semibold tabular-nums ${
                  hlSatir === satirNet(r) ? "text-blue-700" : "text-gray-700"
                }`}
              >
                {satirNet(r)}
              </th>
              {r.hucreler.map((h) => {
                const hl = hlSatir === satirNet(r) && hlSutun === hucreNet(h);
                return (
                  <td
                    key={hucreNet(h)}
                    title={`${nf1.format(h.puan)} puan`}
                    className={`whitespace-nowrap border-b border-gray-100 px-2 py-1 text-center tabular-nums ${BANT[h.bant] || BANT.gri} ${
                      hl ? "ring-2 ring-inset ring-blue-500" : ""
                    }`}
                  >
                    {h.yerGosterim || h.gosterim}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Sayfa ──
interface RoadmapProps {
  // Plan bu sıraya göre yeniden kurulunca (Bugün/Planım sekmeleri tazelensin diye)
  onPlanRebuilt?: () => void;
}

export default function Roadmap({ onPlanRebuilt }: RoadmapProps) {
  const navigate = useNavigate();
  const [data, setData] = useState<YksRoadmapResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Hedef formu. Net alanları oturum sırasına göre: YKS'de TYT/AYT, LGS'de Sözel/Sayısal.
  const [puanTuru, setPuanTuru] = useState<YksPuanTuru | null>(null);
  const [hedefText, setHedefText] = useState("");
  const [net1Text, setNet1Text] = useState("");
  const [net2Text, setNet2Text] = useState("");
  const [dinMuaf, setDinMuaf] = useState(false);
  // Öğrenci forma dokunduysa sessiz yenilemeler (beyan, plan kurma) yazdıklarını ezmesin.
  const [dirty, setDirty] = useState(false);
  // Net alanlarının sunucudan gelen hali — değişmediyse kayıtta mevcutNetler gönderilmez
  // (tanışma sohbetinin ders bazlı netleri toplam olarak düzleşmesin).
  const prefillRef = useRef({ net1: "", net2: "" });
  const loadSeq = useRef(0);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Planı bu sıraya göre kur / gerçekçilik önerisini uygula / konu beyanı
  const [rebuilding, setRebuilding] = useState(false);
  const [rebuilt, setRebuilt] = useState(false);
  const [applyingHedef, setApplyingHedef] = useState<number | null>(null);
  const [beyanBusy, setBeyanBusy] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  // Açık/kapalı durumlar
  const [expandedDers, setExpandedDers] = useState<Set<string> | null>(null);
  const [expandedKonu, setExpandedKonu] = useState<Set<string>>(new Set());

  // Senaryo matrisi — ilk açılışta yüklenir
  const [scenOpen, setScenOpen] = useState(false);
  const [scen, setScen] = useState<YksScenarioResponse | null>(null);
  const [scenLoading, setScenLoading] = useState(false);
  const [scenError, setScenError] = useState<string | null>(null);

  const load = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const seq = ++loadSeq.current;
      const res = await ApiService.getCoachRoadmap();
      if (seq !== loadSeq.current) return; // daha yeni bir yükleme başladı
      setData(res);
    } catch (e: any) {
      setError(e?.message || "Yol haritası yüklenemedi");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Sınava göre değişenler — sunucudan gelir, ekran hiçbir sınavı varsaymaz.
  const lgs = data?.sinav === "LGS";
  const yuzdelikHedef = data?.hedefBirimi === "yuzdelik";
  const oturumlar = useMemo(() => data?.oturumlar || ["TYT", "AYT"], [data?.oturumlar]);
  const oturumTestleri = data?.oturumTestleri || {};
  // YKS-TYT puan türünde ikinci oturum (AYT) sorulmaz.
  const ikinciOturumVar = oturumlar.length > 1 && !(data?.sinav !== "LGS" && puanTuru === "TYT");
  const yerAdi = yuzdelikHedef ? "dilim" : "sıralama";

  // Sunucudan gelen hedef → form alanları; ders kartlarında ilk 3'ü açık başlat.
  const srvPuanTuru = data?.puanTuru || null;
  const srvHedef = data?.hedefSiralama || null;
  const srvYuzdelik = data?.hedefYuzdelik ?? null;
  const srvDinMuaf = !!data?.dinMuaf;
  const srvNetKey = JSON.stringify(data?.mevcutNetler ?? null);
  useEffect(() => {
    if (!data || dirty) return;
    setPuanTuru(srvPuanTuru);
    setHedefText(yuzdelikHedef ? yuzdeText(srvYuzdelik) : srvHedef ? nfInt.format(srvHedef) : "");
    const n1 = netText(netToplam(data.mevcutNetler, oturumlar[0], oturumTestleri[oturumlar[0]]));
    const n2 = netText(netToplam(data.mevcutNetler, oturumlar[1], oturumTestleri[oturumlar[1]]));
    setNet1Text(n1);
    setNet2Text(n2);
    prefillRef.current = { net1: n1, net2: n2 };
    setDinMuaf(srvDinMuaf);
  }, [srvPuanTuru, srvHedef, srvYuzdelik, srvDinMuaf, srvNetKey, dirty]); // data kasıtlı dışarıda: yalnız sunucu değerleri değişince senkron

  // Ders kartlarında ilk 3'ü açık başlat (hedef sonradan kaydedilince de).
  useEffect(() => {
    if (!data?.dersler?.length) return;
    setExpandedDers((prev) => {
      if (prev && prev.size) return prev;
      return new Set(data.dersler!.slice(0, 3).map((d) => `${d.test}-${d.ders}`));
    });
  }, [data]);

  // Senaryo: açıkken ve puan türü değişmişse yeniden çek.
  useEffect(() => {
    if (!scenOpen || !data?.puanTuru) return;
    if (scen && scen.puanTuru === data.puanTuru) return;
    let alive = true;
    (async () => {
      setScenLoading(true);
      setScenError(null);
      try {
        const res = await ApiService.getCoachScenario(data.puanTuru!);
        if (alive) setScen(res);
      } catch (e: any) {
        if (alive) setScenError(e?.message || "Senaryolar yüklenemedi");
      } finally {
        if (alive) setScenLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [scenOpen, data?.puanTuru, scen]);

  const konuById = useMemo(() => {
    const m = new Map<string, YksKonu>();
    for (const k of data?.konular || []) m.set(k.konuId, k);
    for (const d of data?.dersler || []) for (const k of d.konular) if (!m.has(k.konuId)) m.set(k.konuId, k);
    return m;
  }, [data]);

  const resolveKonu = useCallback((id: string) => konuById.get(id)?.konu || id, [konuById]);

  const toggleDers = (key: string) =>
    setExpandedDers((prev) => {
      const next = new Set(prev || []);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleKonu = (id: string) =>
    setExpandedKonu((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const study = (lessonId: string) => navigate(`/lessons/play/${lessonId}`);

  // Eşzamanlı yazma yok: hedef öneri uygulanırken "kaydet"e basılırsa eski değer geri yazılırdı.
  const busy = saving || clearing || rebuilding || applyingHedef !== null || beyanBusy !== null;

  const save = async () => {
    if (busy) return;
    // LGS'de puan türü yok; sunucu kendi doldurur.
    if (!puanTuru && !lgs) {
      setFormError("Önce puan türünü seç.");
      return;
    }
    const hedef = yuzdelikHedef ? parseYuzde(hedefText) : parseTamsayi(hedefText);
    if (hedefText.trim() && (hedef === null || hedef < (yuzdelikHedef ? 0.01 : 1))) {
      setFormError(yuzdelikHedef ? "Hedef dilim %0,01 ile %100 arasında olmalı." : "Hedef sıralama 1'den büyük bir sayı olmalı.");
      return;
    }
    const n1 = parseNet(net1Text);
    const n2 = ikinciOturumVar ? parseNet(net2Text) : null;

    setSaving(true);
    setFormError(null);
    setRebuilt(false);
    try {
      await ApiService.updateCoachProfile({
        ...(puanTuru ? { puanTuru } : {}),
        // boş bırakıldıysa mevcut hedefe dokunma — kaldırmak için ayrı düğme var
        ...(hedef !== null ? (yuzdelikHedef ? { hedefYuzdelik: hedef } : { hedefSiralama: hedef }) : {}),
        ...(net1Text !== prefillRef.current.net1 || net2Text !== prefillRef.current.net2
          ? { mevcutNetler: n1 === null && n2 === null ? null : { [oturumlar[0]]: n1, ...(ikinciOturumVar ? { [oturumlar[1]]: n2 } : {}) } }
          : {}),
        dinMuaf: puanTuru === "SÖZ" ? dinMuaf : false,
      });
      setDirty(false);
      await load(true);
    } catch (e: any) {
      setFormError(e?.message || "Hedef kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const clearTarget = async () => {
    if (!window.confirm(`Hedef ${yerAdi}in kaldırılacak. Devam edilsin mi?`)) return;
    setClearing(true);
    setFormError(null);
    try {
      await ApiService.updateCoachProfile({ hedefSiralama: null });
      await load(true);
    } catch (e: any) {
      setFormError(e?.message || "Hedef kaldırılamadı");
    } finally {
      setClearing(false);
    }
  };

  // Planı yol haritası sırasına göre yeniden kur (sunucu hedef kaydında planı kendiliğinden kurmaz).
  const rebuildPlan = async () => {
    if (busy) return;
    setRebuilding(true);
    setRebuilt(false);
    setFormError(null);
    try {
      await ApiService.createCoachPlan();
      await load(true);
      setRebuilt(true);
      onPlanRebuilt?.();
    } catch (e: any) {
      setFormError(e?.message || "Plan kurulamadı");
    } finally {
      setRebuilding(false);
    }
  };

  // Gerçekçilik önerisi: önerilen / ara hedefi tek tıkla hedef yap.
  const applyHedef = async (n: number) => {
    if (busy) return;
    setApplyingHedef(n);
    setFormError(null);
    setRebuilt(false);
    try {
      await ApiService.updateCoachProfile({ hedefSiralama: n });
      await load(true);
    } catch (e: any) {
      setFormError(e?.message || "Hedef güncellenemedi");
    } finally {
      setApplyingHedef(null);
    }
  };

  // Konu beyanı: "Biliyorum" ↔ kaldır; etiket/sıra değişebileceği için harita yeniden çekilir.
  const setBeyan = async (konuId: string, durum: YksBeyanDurumu | null) => {
    if (busy) return;
    setBeyanBusy(konuId);
    setListError(null);
    try {
      await ApiService.setKonuBeyan(konuId, durum);
      await load(true);
    } catch (e: any) {
      setListError(e?.message || "Beyan kaydedilemedi");
    } finally {
      setBeyanBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="grid min-h-[40vh] place-items-center">
        <div className="h-9 w-9 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-5">
        <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          <AlertCircle className="h-5 w-5 shrink-0" /> {error || "Yol haritası yüklenemedi"}
          <button onClick={() => load()} className="ml-auto text-xs font-semibold underline">
            Tekrar dene
          </button>
        </div>
      </div>
    );
  }

  // Kapsam dışı hedef (okul sınavı): hedef kartı anlamsız — açıklama göster, kart gösterme.
  if (data.uygulanabilir === false) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-5 sm:px-6">
        <div className="rounded-lg border border-gray-200 bg-white p-6 text-center">
          <Target className="mx-auto mb-2 h-8 w-8 text-gray-300" />
          <p className="text-sm font-medium text-gray-900">Yol haritası YKS ve LGS hedefleri için</p>
          <p className="mx-auto mt-1 max-w-md text-xs text-gray-500">
            {data.uygulanamaNedeni || "Planın başka bir hedef için kurulu. Koçun Bugün ve Planım sekmelerinde seninle."}
          </p>
        </div>
      </div>
    );
  }

  const hasHedef = data.hasHedef;
  const ozet = data.ozet;
  const mevcut = data.mevcut || null;
  const dersler = data.dersler || [];
  const currentNet1 = netToplam(data.mevcutNetler, oturumlar[0], oturumTestleri[oturumlar[0]]);
  const currentNet2 = netToplam(data.mevcutNetler, oturumlar[1], oturumTestleri[oturumlar[1]]);
  const kaynaklar = data.kaynaklar || {};
  const denemeler = data.denemeler || [];
  const gerceklik = hasHedef ? data.gerceklik || null : null;
  const gerekenPuan = typeof data.gerekenPuan === "number" ? data.gerekenPuan : null;

  return (
    <div className="mx-auto w-full max-w-3xl space-y-5 px-4 py-5 sm:px-6">
      {/* Hedef kartı */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-blue-100 text-blue-600">
            <Target className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-gray-400">Hedefim</p>
            <p className="text-base font-semibold leading-snug text-gray-900">
              {hasHedef && data.hedefSiralama
                ? yuzdelikHedef
                  ? `LGS · ${data.hedefGosterim}'lik dilim`
                  : `${data.puanTuru} · ilk ${nfInt.format(data.hedefSiralama)}`
                : yuzdelikHedef
                  ? "Hedef dilimini gir"
                  : "Hedef sıralamanı gir"}
            </p>
            {hasHedef && yuzdelikHedef && data.katilimci && data.hedefSiralama && (
              <p className="text-[11px] text-gray-400">
                ≈ {nfInt.format(data.hedefSiralama)}. sıra · {data.veriYili}'da {nfInt.format(data.katilimci)} öğrenci girdi
              </p>
            )}
          </div>
        </div>

        <div className="mt-4 space-y-3">
          {/* Puan türü — LGS'de tek puan türü var, sorulmaz */}
          <div className={lgs ? "hidden" : ""}>
            <div className="mb-1.5 flex items-center gap-2">
              <label className="text-xs font-medium text-gray-600">Puan türü</label>
              {data.puanTuruKaynagi === "kayit" && (
                <span className="rounded-full border border-gray-200 bg-gray-50 px-1.5 py-px text-[10px] text-gray-500">kayıttan</span>
              )}
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {PUAN_TURLERI.map((t) => {
                const active = puanTuru === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => { setDirty(true); setPuanTuru(t); }}
                    className={`rounded-lg border px-2 py-2 text-sm font-semibold transition-colors ${
                      active
                        ? "border-blue-600 bg-blue-600 text-white"
                        : "border-gray-200 bg-white text-gray-700 hover:border-blue-300"
                    }`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Hedef — YKS'de sıralama, LGS'de yüzdelik dilim */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-600">
              {yuzdelikHedef ? "Hedef yüzdelik dilim" : "Hedef sıralama"}
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={hedefText}
              onChange={(e) => {
                setDirty(true);
                setHedefText(yuzdelikHedef ? e.target.value.replace(/[^\d.,%]/g, "") : e.target.value.replace(/[^\d.]/g, ""));
              }}
              onBlur={() => {
                if (yuzdelikHedef) {
                  const y = parseYuzde(hedefText);
                  setHedefText(yuzdeText(y));
                  return;
                }
                const n = parseTamsayi(hedefText);
                setHedefText(n === null ? "" : nfInt.format(n));
              }}
              placeholder={yuzdelikHedef ? "ör. %5" : "ör. 300.000"}
              className={INPUT_CLS}
            />
            {yuzdelikHedef && (
              <p className="mt-0.5 text-[10px] text-gray-400">
                LGS'de sıralama yerine yüzdelik dilim kullanılır: %1 ≈ en iyi 10.000 öğrenci.
              </p>
            )}
          </div>

          {/* Son deneme netleri */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-600">Son deneme netlerin</label>
            <div className={`grid gap-2 ${ikinciOturumVar ? "grid-cols-2" : "grid-cols-1"}`}>
              <div>
                <input
                  type="text"
                  inputMode="decimal"
                  value={net1Text}
                  onChange={(e) => { setDirty(true); setNet1Text(e.target.value); }}
                  placeholder={`${oturumlar[0]} toplam net`}
                  className={INPUT_CLS}
                />
                <p className="mt-0.5 text-[10px] text-gray-400">
                  {oturumlar[0]} toplam net
                  {lgs && oturumTestleri[oturumlar[0]]?.length ? ` (${oturumTestleri[oturumlar[0]].join(" + ")})` : ""}
                </p>
              </div>
              {ikinciOturumVar && (
                <div>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={net2Text}
                    onChange={(e) => { setDirty(true); setNet2Text(e.target.value); }}
                    placeholder={`${oturumlar[1]} toplam net`}
                    className={INPUT_CLS}
                  />
                  <p className="mt-0.5 text-[10px] text-gray-400">
                    {oturumlar[1]} toplam net
                    {lgs && oturumTestleri[oturumlar[1]]?.length ? ` (${oturumTestleri[oturumlar[1]].join(" + ")})` : ""}
                  </p>
                </div>
              )}
            </div>
          </div>

          {puanTuru === "SÖZ" && (
            <label className="flex items-center gap-2 text-xs text-gray-700">
              <input
                type="checkbox"
                checked={dinMuaf}
                onChange={(e) => { setDirty(true); setDinMuaf(e.target.checked); }}
                className="h-4 w-4 rounded border-gray-300 text-blue-600"
              />
              Din Kültürü'nden muafım
            </label>
          )}

          {formError && (
            <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              <AlertCircle className="h-4 w-4 shrink-0" /> {formError}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={save}
              disabled={busy || (!puanTuru && !lgs)}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
            >
              {saving && <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white" />}
              {saving ? "Kaydediliyor…" : "Hedefimi kaydet"}
            </button>
            {hasHedef && (
              <button
                type="button"
                onClick={clearTarget}
                disabled={busy}
                className="text-xs font-medium text-gray-500 transition-colors hover:text-rose-600 disabled:opacity-60"
              >
                {clearing ? "Kaldırılıyor…" : "Hedefi kaldır"}
              </button>
            )}
          </div>

          {hasHedef && typeof data.gerekenPuan === "number" && (
            <p className="flex items-start gap-1.5 rounded-lg bg-gray-50 p-2.5 text-xs text-gray-600">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
              <span>
                Bu hedef için ≈ <b className="text-gray-800">{nf1.format(data.gerekenPuan)}</b> {lgs ? "LGS" : data.puanTuru} puanı gerekiyor
                {typeof data.hedefteAdaySayisi === "number" && (
                  <>
                    {" "}
                    · {data.veriYili}'da bu {yerAdi}de <b className="text-gray-800">{nfInt.format(data.hedefteAdaySayisi)}</b> kişi vardı
                  </>
                )}
              </span>
            </p>
          )}

          {/* Hedef kaydedince plan kendiliğinden kurulmaz — öğrenci onaylar */}
          {hasHedef && (
            <div className="border-t border-gray-100 pt-3">
              <button
                type="button"
                onClick={rebuildPlan}
                disabled={busy}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-60 sm:w-auto"
              >
                <RefreshCw className={`h-4 w-4 ${rebuilding ? "animate-spin" : ""}`} />
                {rebuilding ? "Plan kuruluyor…" : "Planı bu sıraya göre kur"}
              </button>
              {rebuilt ? (
                <p className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Planın yenilendi; Bugün sekmesinde görebilirsin.
                </p>
              ) : (
                <p className="mt-1.5 text-[10px] text-gray-400">Bugün ve Planım sekmelerindeki kalemler bu konu sırasına göre yeniden dizilir.</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Neredesin — deneme / beyan / testlerden kanıtlı */}
      <NeredesinPanel data={data} />

      {/* Gerçekçilik — hedef mevcut duruma göre ne kadar uzak */}
      {gerceklik && (
        <div className={`rounded-lg border p-3 text-xs ${GERCEKLIK[gerceklik.durum] || GERCEKLIK.bilinmiyor}`}>
          <p className="flex items-start gap-1.5">
            <Info className="mt-px h-3.5 w-3.5 shrink-0 opacity-70" />
            <span>{gerceklik.mesaj}</span>
          </p>
          {(typeof gerceklik.onerilenHedef === "number" || typeof gerceklik.araHedef === "number") && (
            <div className="mt-2 flex flex-wrap gap-2 pl-5">
              {typeof gerceklik.onerilenHedef === "number" && (
                <button
                  type="button"
                  onClick={() => applyHedef(gerceklik.onerilenHedef!)}
                  disabled={busy}
                  className="rounded-md border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60"
                >
                  {applyingHedef === gerceklik.onerilenHedef
                    ? "Kaydediliyor…"
                    : `Hedefi ${gerceklik.onerilenHedefGosterim || nfInt.format(gerceklik.onerilenHedef)} yap`}
                </button>
              )}
              {typeof gerceklik.araHedef === "number" && (
                <button
                  type="button"
                  onClick={() => applyHedef(gerceklik.araHedef!)}
                  disabled={busy}
                  className="rounded-md border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60"
                >
                  {applyingHedef === gerceklik.araHedef
                    ? "Kaydediliyor…"
                    : `Ara hedefi ${gerceklik.araHedefGosterim || nfInt.format(gerceklik.araHedef)} yap`}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Denemelerim */}
      {denemeler.length > 0 && (
        <DenemelerCard denemeler={denemeler} hasHedef={hasHedef} gerekenPuan={gerekenPuan} oturumlar={oturumlar} />
      )}

      {/* Hedef yoksa boş durum */}
      {!hasHedef && (
        <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/60 p-8 text-center">
          <Target className="mx-auto mb-2 h-8 w-8 text-gray-300" />
          <p className="text-sm font-medium text-gray-900">Hedefini gir, sana hangi konuya önce çalışacağını söyleyeyim.</p>
          <p className="mx-auto mt-1 max-w-sm text-xs text-gray-500">
            {yuzdelikHedef
              ? "Hedef dilimini yaz; konuları çıkma sıklığına ve puan katkısına göre sıralayayım."
              : "Puan türünü ve hedef sıralamanı yaz; konuları çıkma sıklığına ve puan katkısına göre sıralayayım."}
          </p>
        </div>
      )}

      {/* Durum şeridi */}
      {hasHedef && ozet && (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-lg border border-gray-200 bg-white p-3">
              <p className="text-[11px] text-gray-400">Gereken puan</p>
              <p className="text-base font-bold text-gray-900">{typeof data.gerekenPuan === "number" ? nf1.format(data.gerekenPuan) : "—"}</p>
              <p className="truncate text-[10px] text-gray-400">{data.puanTuru}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-3">
              <p className="text-[11px] text-gray-400">Şu anki puan</p>
              <p className="text-base font-bold text-gray-900">{mevcut ? nf1.format(mevcut.puan) : "—"}</p>
              <p className="truncate text-[10px] text-gray-400" title={mevcut ? `${mevcut.bant.gosterim} ${yerAdi} bandı` : undefined}>
                {mevcut ? `${mevcut.bant.gosterim} ${yerAdi} bandı` : "deneme neti gir"}
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-3">
              <p className="text-[11px] text-gray-400">Puan açığı</p>
              {mevcut ? (
                mevcut.hedefTutuyor ? (
                  <p className="inline-flex items-center gap-1 text-xs font-bold leading-tight text-emerald-600">
                    <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> Hedef tutuyor
                  </p>
                ) : (
                  <p className="text-base font-bold text-amber-600">{nf1.format(mevcut.puanAcigi ?? 0)}</p>
                )
              ) : (
                <p className="text-base font-bold text-gray-900">—</p>
              )}
              <p className="truncate text-[10px] text-gray-400">{mevcut && !mevcut.hedefTutuyor ? "puan" : " "}</p>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-center justify-between gap-2 text-xs">
              <span className="text-gray-700">
                Öncelikli konular puanın <b>%{pct(ozet.kapsamaOrani)}</b>'ini kapsıyor
              </span>
              <span className="shrink-0 text-[11px] text-gray-400">
                {oturumlar
                  .filter((o) => ozet.oturumKapsama?.[o] !== undefined && (ikinciOturumVar || o === oturumlar[0]))
                  .map((o) => `${o} %${pct(ozet.oturumKapsama[o])}`)
                  .join(" · ")}
              </span>
            </div>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-100">
              <div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.min(100, pct(ozet.kapsamaOrani))}%` }} />
            </div>
            <div className="mt-2.5 flex flex-wrap gap-1.5 text-[11px]">
              <span className={`rounded-full border px-2 py-0.5 font-medium ${ETIKET.ONCELIKLI.chip}`}>{ozet.oncelikli} öncelikli</span>
              <span className={`rounded-full border px-2 py-0.5 font-medium ${ETIKET.SONRA.chip}`}>{ozet.sonra} sonra</span>
              <span className={`rounded-full border px-2 py-0.5 font-medium ${ETIKET.ATLANABILIR.chip}`}>{ozet.atlanabilir} atlanabilir</span>
              <span className="self-center text-gray-400">· {ozet.toplamKonu} konu</span>
            </div>

            {data.atlamaKapali && (
              <p className="mt-2.5 flex items-start gap-1.5 text-[11px] text-gray-500">
                <Info className="mt-px h-3.5 w-3.5 shrink-0 text-gray-400" />
                {lgs
                  ? "LGS'de 90 sorunun hepsi sayılır: hiçbir konu atlanmıyor, sadece sıra öneriliyor."
                  : "Hedefin 100.000'den iyi olduğu için hiçbir konu atlanmıyor; sadece sıra öneriliyor."}
              </p>
            )}
            {!!data.icerik?.oncelikliIceriksiz && (
              <p className="mt-2.5 flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800">
                <AlertCircle className="mt-px h-3.5 w-3.5 shrink-0" />
                Öncelikli {data.icerik.oncelikliIceriksiz} konunun içeriği henüz sistemde yok; yüklendikçe planına girecek.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Ders listesi */}
      {hasHedef && (
        <div>
          <div className="mb-2 flex items-center gap-2">
            <h2 className="text-base font-semibold text-gray-900">Konu sırası</h2>
            <span className="text-sm text-gray-400">{dersler.length} ders</span>
          </div>
          {listError && (
            <div className="mb-2 flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              <AlertCircle className="h-4 w-4 shrink-0" /> {listError}
            </div>
          )}
          {dersler.length === 0 ? (
            <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-gray-600">
              {lgs ? "Konu listesi bulunamadı." : "Bu puan türü için konu listesi bulunamadı."}
            </div>
          ) : (
            <div className="space-y-2">
              {dersler.map((d) => {
                const key = `${d.test}-${d.ders}`;
                return (
                  <DersCard
                    key={key}
                    ders={d}
                    expanded={!!expandedDers?.has(key)}
                    onToggle={() => toggleDers(key)}
                    expandedKonu={expandedKonu}
                    onToggleKonu={toggleKonu}
                    resolveKonu={resolveKonu}
                    onStudy={study}
                    onBeyan={setBeyan}
                    beyanBusy={beyanBusy}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Net senaryoları */}
      {hasHedef && data.puanTuru && (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <button
            type="button"
            onClick={() => setScenOpen((v) => !v)}
            className="flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-gray-50 sm:px-4"
          >
            <Table2 className="h-4 w-4 shrink-0 text-blue-600" />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-gray-900">Net senaryoları</span>
              <span className="block text-[11px] text-gray-400">
                Hangi net hangi {yerAdi}i getirir{lgs ? "" : ` · ${data.puanTuru}`}
              </span>
            </span>
            <ChevronDown className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${scenOpen ? "rotate-180" : ""}`} />
          </button>

          {scenOpen && (
            <div className="border-t border-gray-100 p-3 sm:p-4">
              {scenLoading ? (
                <div className="grid place-items-center py-6">
                  <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-blue-600" />
                </div>
              ) : scenError ? (
                <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
                  <AlertCircle className="h-4 w-4 shrink-0" /> {scenError}
                </div>
              ) : scen ? (
                <>
                  <ScenarioBlock
                    data={scen}
                    currentSatir={oturumlar[0] === scen.satirOturum ? currentNet1 : currentNet2}
                    currentSutun={oturumlar[0] === scen.satirOturum ? currentNet2 : currentNet1}
                    yerBasligi={yuzdelikHedef ? "Dilim" : "Sıralama"}
                  />
                  <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-gray-400">
                    <span>
                      Satır: {scen.satirOturum} net{scen.sutunOturum && ` · Sütun: ${scen.sutunOturum} net`}
                    </span>
                    <span>· Hücre: yaklaşık {yerAdi}; puan için üzerine gel</span>
                    {(currentNet1 !== null || currentNet2 !== null) && (
                      <span className="inline-flex items-center gap-1">
                        · <span className="inline-block h-3 w-3 rounded-sm ring-2 ring-inset ring-blue-500" /> son denemen
                      </span>
                    )}
                  </div>
                  {scen.uyari && scen.uyari !== data.uyari && <p className="mt-1.5 text-[10px] text-gray-400">{scen.uyari}</p>}
                </>
              ) : null}
            </div>
          )}
        </div>
      )}

      {/* Alt bilgi — kaynak listesi sınava göre değişir (ÖSYM / MEB), adlar pakette tanımlı */}
      <div className="space-y-1.5 border-t border-gray-100 pt-4 text-[11px] text-gray-500">
        {data.uyari && <p>{data.uyari}</p>}
        <p>
          Veriler yaklaşıktır; {lgs ? "MEB" : "ÖSYM"} konu dağılımı ve net katsayısı yayımlamaz.
        </p>
        <p className="flex flex-wrap gap-x-4 gap-y-1">
          {Object.entries(kaynaklar)
            .filter(([, v]) => typeof v === "string" && /^https?:\/\//.test(v))
            .map(([k, v]) => (
              <a
                key={k}
                href={v as string}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-medium text-blue-600 hover:text-blue-700"
              >
                {KAYNAK_ADI[k] || k.replace(/_/g, " ")} <ExternalLink className="h-3 w-3" />
              </a>
            ))}
        </p>
      </div>
    </div>
  );
}
